import { Capacitor } from '@capacitor/core';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';
import { capSQLiteSet, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';

import { chunkArray } from 'src/utils/chunk-array';
import { safeJSONParse } from 'src/utils/safe-json';

import type { BusinessV3Item } from 'src/sections/admin-page/ListView/Businesses/types';

import type { SystemConfigSnapshot, ConditionalConfigDeletion } from './system-config-snapshot';
import {
  DiagnosticIODeferred,
  type DiagnosticAdmission,
  assertDiagnosticAdmission,
} from './diagnostic-admission';
import {
  type DiagnosticScope,
  type DiagnosticDropAlert,
  type DiagnosticUploadBatch,
  type DiagnosticDeliveryState,
  DIAGNOSTIC_DROP_HISTORY_LIMIT,
  type DiagnosticDeliveryMutation,
} from './diagnostic-delivery-types';
import {
  InventoryStats,
  SystemConfigType,
  OfflineMutationRecord,
  DiagnosticLogRecordRow,
  DiagnosticLogPageQuery,
  NewOfflineMutationRecord,
  DiagnosticLogStorageStats,
  NewDiagnosticLogRecordRow,
  DiagnosticLogRecordPayload,
  CompletedSaleReportOutboxRecord,
  CompletedSaleReportHistoryRecord,
  COMPLETED_SALE_REPORT_HISTORY_LIMIT,
  RestaurantOrderEnrichmentCacheRecord,
} from './types';

const INVENTORY_UPSERT_SQL = `INSERT OR REPLACE INTO inventory
   (product_id, identifier, name, item_category, stock, price, sold, deleted_at, data, variable_price_lookup_code)
   VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)` as const;

function toDeletedAtStr(deletedAt: ProductDataType['deletedAt']): string | null {
  if (!deletedAt) return null;
  if (deletedAt instanceof Date) return deletedAt.toISOString();
  return String(deletedAt);
}

function buildInventoryValues(product: ProductDataType): unknown[] {
  return [
    product.id,
    product.identifier ?? '',
    product.name ?? '',
    product.itemCategory ?? 'GENERAL',
    product.currentStockLevel ?? 0,
    product.price ?? 0,
    product.sold ? 1 : 0,
    toDeletedAtStr(product.deletedAt),
    JSON.stringify(product),
    product.attributes?.physicalGood?.variablePriceLookupCode ?? '',
  ];
}

function buildInventorySet(product: ProductDataType): capSQLiteSet {
  return {
    statement: INVENTORY_UPSERT_SQL,
    values: buildInventoryValues(product),
  };
}

// Duplicate rows can share an identifier (server-side duplicates, legacy ghost
// rows). Always resolve to the most recently updated copy so native and web
// agree on which duplicate wins. Done in JS rather than json_extract because
// JSON1 availability varies across the capacitor/jeep SQLite stack.
function pickNewestInventoryRow(
  rows: Array<{ data: string }> | undefined,
  context: string
): ProductDataType | null {
  if (!rows || rows.length === 0) return null;
  const newest = rows.reduce<{ data: ProductDataType; time: number } | null>((best, row) => {
    const parseResult = safeJSONParse<ProductDataType>(row.data, context);
    if (!parseResult.success) return best;
    const time = new Date(parseResult.data.updatedAt).getTime();
    const comparable = Number.isNaN(time) ? 0 : time;
    return !best || comparable > best.time ? { data: parseResult.data, time: comparable } : best;
  }, null);
  return newest?.data ?? null;
}

class SQLiteService {
  private sqliteConnection: SQLiteConnection;

  private db: SQLiteDBConnection | null = null;

  private initialized = false;

  private readonly databaseName: string;

  private databaseReadyPromise: Promise<void> | null = null;

  // The SQLiteService is constructed with an open SQLiteDBConnection and the SQLiteConnection manager.
  constructor(
    sqliteConnection: SQLiteConnection,
    dbConnection: SQLiteDBConnection,
    databaseName = 'mydb'
  ) {
    console.log('SQLiteService constructor – injecting DB connection');
    this.sqliteConnection = sqliteConnection;
    this.db = dbConnection;
    this.databaseName = databaseName;
    this.initialized = true;
  }

  // Ensure all tables and indexes exist in the SQLite DB
  private async checkAndCreateDatabase(): Promise<void> {
    if (!this.db) {
      throw new Error('Database connection is not available');
    }

    // system_config table
    const res = await this.db.query(
      'SELECT name FROM sqlite_master WHERE type="table" AND name="system_config"'
    );
    if (!res.values || res.values.length === 0) {
      await this.db.execute(
        'CREATE TABLE IF NOT EXISTS system_config ' +
          '(id INTEGER PRIMARY KEY AUTOINCREMENT, type TEXT UNIQUE, data JSON)'
      );
    }

    // inventory table
    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS inventory (
        product_id                TEXT PRIMARY KEY,
        identifier                TEXT NOT NULL DEFAULT '',
        name                      TEXT NOT NULL DEFAULT '',
        item_category             TEXT NOT NULL DEFAULT 'GENERAL',
        stock                     REAL NOT NULL DEFAULT 0,
        price                     REAL NOT NULL DEFAULT 0,
        sold                      INTEGER NOT NULL DEFAULT 0,
        deleted_at                TEXT,
        data                      TEXT NOT NULL,
        variable_price_lookup_code TEXT NOT NULL DEFAULT ''
      )`
    );

    // Add variable_price_lookup_code column for databases created before it existed.
    const colCheck = await this.db.query(
      `SELECT COUNT(*) as cnt FROM pragma_table_info('inventory') WHERE name = 'variable_price_lookup_code'`
    );
    if ((colCheck.values?.[0]?.cnt ?? 0) === 0) {
      await this.db.execute(
        `ALTER TABLE inventory ADD COLUMN variable_price_lookup_code TEXT NOT NULL DEFAULT ''`
      );
    }

    // One-time backfill: populate variable_price_lookup_code from JSON data for existing rows.
    // Runs only for rows where the column is still empty (i.e. pre-migration imports).
    const toBackfill = await this.db.query(
      `SELECT product_id, data FROM inventory WHERE variable_price_lookup_code = ''`
    );
    if (toBackfill.values && toBackfill.values.length > 0) {
      console.log(
        `[SQLiteService] Backfilling variable_price_lookup_code for ${toBackfill.values.length} rows`
      );
      const backfillSet: capSQLiteSet[] = toBackfill.values
        .map((row: any) => {
          const parsed = safeJSONParse<any>(row.data, 'SQLite:backfill-vlpc');
          let vlpc = '';
          if (parsed.success) {
            const physicalGood = parsed.data?.attributes?.physicalGood;
            // Use the stored value if present, otherwise derive from identifier for variable-price items
            vlpc =
              physicalGood?.variablePriceLookupCode ??
              (physicalGood?.variablePrice === true && (parsed.data?.identifier?.length ?? 0) >= 5
                ? String(parsed.data.identifier).substring(0, 5)
                : '');
          }
          return { productId: row.product_id as string, vlpc };
        })
        .filter(({ vlpc }) => vlpc !== '')
        .map(({ productId, vlpc }) => ({
          statement: `UPDATE inventory SET variable_price_lookup_code = ? WHERE product_id = ?`,
          values: [vlpc, productId],
        }));
      if (backfillSet.length > 0) {
        await this.db.executeSet(backfillSet, true);
      }
      console.log('[SQLiteService] variable_price_lookup_code backfill complete');
    }

    // indexes for inventory
    await this.db.execute('CREATE INDEX IF NOT EXISTS idx_inventory_name ON inventory(name)');
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_inventory_identifier ON inventory(identifier)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_inventory_category ON inventory(item_category)'
    );
    // Composite index speeds up the category-counts GROUP BY query
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_inventory_category_active ON inventory(item_category, deleted_at)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_inventory_vlpc ON inventory(variable_price_lookup_code)'
    );

    // business_inventory_stats — derived cache, recalculated from inventory
    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS business_inventory_stats (
        id                   TEXT PRIMARY KEY,
        total_items          INTEGER NOT NULL DEFAULT 0,
        total_active_items   INTEGER NOT NULL DEFAULT 0,
        total_deleted_items  INTEGER NOT NULL DEFAULT 0,
        total_stock_quantity REAL    NOT NULL DEFAULT 0,
        category_counts      TEXT    NOT NULL DEFAULT '[]',
        last_calculated_at   TEXT    NOT NULL,
        inventory_version    INTEGER
      )`
    );

    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS restaurant_order_enrichment_cache (
        cache_key         TEXT PRIMARY KEY,
        business_id       TEXT NOT NULL,
        sale_id           TEXT NOT NULL,
        payload           TEXT NOT NULL,
        source_updated_at TEXT,
        fetched_at        TEXT NOT NULL,
        expires_at        TEXT NOT NULL,
        UNIQUE (business_id, sale_id)
      )`
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_restaurant_order_cache_expiry ON restaurant_order_enrichment_cache(expires_at)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_restaurant_order_cache_business ON restaurant_order_enrichment_cache(business_id)'
    );

    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS completed_sale_report_outbox (
        report_idempotency_key TEXT PRIMARY KEY,
        business_id            TEXT NOT NULL,
        state                  TEXT NOT NULL,
        created_at             TEXT NOT NULL,
        handed_off_at          TEXT,
        data                   TEXT NOT NULL
      )`
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_completed_report_outbox_business_state ON completed_sale_report_outbox(business_id, state)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_completed_report_outbox_created ON completed_sale_report_outbox(created_at)'
    );

    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS completed_sale_report_history (
        report_idempotency_key TEXT PRIMARY KEY,
        business_id            TEXT NOT NULL,
        resolution             TEXT NOT NULL,
        completed_at           TEXT NOT NULL,
        data                   TEXT NOT NULL
      )`
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_completed_report_history_business_completed ON completed_sale_report_history(business_id, completed_at)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_completed_report_history_completed ON completed_sale_report_history(completed_at)'
    );

    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS offline_mutation_outbox (
        sequence           INTEGER PRIMARY KEY AUTOINCREMENT,
        mutation_id        TEXT NOT NULL UNIQUE,
        business_id        TEXT NOT NULL,
        business_device_id TEXT NOT NULL,
        operation          TEXT NOT NULL,
        state              TEXT NOT NULL,
        created_at         TEXT NOT NULL,
        next_attempt_at    TEXT,
        data               TEXT NOT NULL
      )`
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_offline_mutation_tenant_state_sequence ON offline_mutation_outbox(business_id, state, sequence)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_offline_mutation_device_sequence ON offline_mutation_outbox(business_device_id, sequence)'
    );

    await this.db.execute(
      `CREATE TABLE IF NOT EXISTS diagnostic_log_records (
        seq          INTEGER PRIMARY KEY AUTOINCREMENT,
        record_id    TEXT NOT NULL UNIQUE,
        business_id  TEXT NOT NULL,
        session_id   TEXT NOT NULL,
        occurred_at  TEXT NOT NULL,
        level        TEXT NOT NULL,
        channel      TEXT NOT NULL,
        state        TEXT NOT NULL,
        size_bytes   INTEGER NOT NULL,
        data         TEXT NOT NULL
      )`
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_diagnostic_log_business_state_seq ON diagnostic_log_records(business_id, state, seq)'
    );
    await this.db.execute(
      'CREATE INDEX IF NOT EXISTS idx_diagnostic_log_occurred ON diagnostic_log_records(occurred_at)'
    );
    await this.db.execute(`CREATE TABLE IF NOT EXISTS diagnostic_upload_batches (
      id TEXT PRIMARY KEY, business_id TEXT NOT NULL, device_id TEXT NOT NULL, data TEXT NOT NULL
    )`);
    await this.db.execute(`CREATE TABLE IF NOT EXISTS diagnostic_delivery_state (
      id TEXT PRIMARY KEY, business_id TEXT NOT NULL, device_id TEXT NOT NULL, data TEXT NOT NULL
    )`);
    await this.db.execute(`CREATE TABLE IF NOT EXISTS diagnostic_drop_alerts (
      seq INTEGER PRIMARY KEY AUTOINCREMENT, id TEXT UNIQUE NOT NULL,
      business_id TEXT NOT NULL, device_id TEXT NOT NULL, data TEXT NOT NULL
    )`);
  }

  private async openDatabaseConnection(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') {
      throw new Error('Unsupported platform for SQLiteService.initializeDB');
    }

    const isConn = (await this.sqliteConnection.isConnection(this.databaseName, false)).result;
    if (isConn) {
      await this.sqliteConnection.closeAllConnections();
    }
    const dbConnection = await this.sqliteConnection.createConnection(
      this.databaseName,
      false,
      'no-encryption',
      1,
      false
    );
    await dbConnection.open();
    this.db = dbConnection;
    this.initialized = true;
  }

  private async initializeDatabaseAndSchema(): Promise<void> {
    if (!this.initialized || !this.db) {
      await this.openDatabaseConnection();
    }
    await this.checkAndCreateDatabase();
  }

  // Resolves only after the connection and its complete schema are ready.
  public async initializeDB(): Promise<void> {
    if (!this.databaseReadyPromise) {
      this.databaseReadyPromise = this.initializeDatabaseAndSchema();
    }

    const readyPromise = this.databaseReadyPromise;
    try {
      await readyPromise;
    } catch (error) {
      if (this.databaseReadyPromise === readyPromise) {
        this.databaseReadyPromise = null;
      }
      console.error('Error initializing SQLite DB:', error);
      throw error;
    }
  }

  // Retrieve the database connection only after initialization and migrations complete.
  private async getDB(): Promise<SQLiteDBConnection> {
    await this.initializeDB();
    if (!this.db) {
      throw new Error('Database connection is not available');
    }
    return this.db;
  }

  public async closeConnection(): Promise<void> {
    if (this.db) {
      try {
        console.log('Closing SQLite DB connection');
        await this.db.close();
        console.log('SQLite DB connection closed');
      } catch (error) {
        console.error('Error closing SQLite DB connection:', error);
      } finally {
        this.db = null;
        this.initialized = false;
        this.databaseReadyPromise = null;
      }
    }
    // Ensure all native connections are closed (especially if multiple were open)
    try {
      await this.sqliteConnection.closeAllConnections();
      console.log('All SQLite connections closed');
    } catch (error) {
      console.error('Error closing all SQLite connections:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // system_config CRUD
  // ---------------------------------------------------------------------------

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    try {
      const db = await this.getDB();
      const selectQuery = 'SELECT id FROM system_config WHERE type = ?';
      const result = await db.query(selectQuery, [type]);
      if (result.values && result.values.length > 0) {
        const updateQuery = 'UPDATE system_config SET data = ? WHERE type = ?';
        await db.run(updateQuery, [JSON.stringify(data), type]);
      } else {
        const insertQuery = 'INSERT INTO system_config (type, data) VALUES (?, ?)';
        await db.run(insertQuery, [type, JSON.stringify(data)]);
      }
    } catch (error) {
      console.error('Error in updateOrCreateSystemConfig:', error);
    }
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    try {
      const db = await this.getDB();
      const query = 'SELECT * FROM system_config WHERE type = ?';
      const res = await db.query(query, [type]);

      if (res.values && res.values.length > 0) {
        const row = res.values[0];

        // Safe JSON parsing with context
        const parseResult = safeJSONParse<T>(row.data, `SQLite:getSystemConfig:${type}`);

        if (!parseResult.success) {
          console.error(`Failed to parse system config '${type}':`, parseResult.error);
          // Log the corrupted data for debugging
          console.error('Corrupted data (first 200 chars):', row.data?.substring(0, 200));
          return null;
        }

        return {
          id: row.id,
          type: row.type,
          data: parseResult.data,
        };
      }
      return null;
    } catch (error) {
      console.error('Error in getSystemConfig:', error);
      return null;
    }
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    try {
      const db = await this.getDB();
      const query = 'SELECT * FROM system_config';
      const res = await db.query(query);

      if (!res.values) return [];

      // Filter out configs with parse errors instead of failing completely
      const configs: SystemConfigType<T>[] = res.values.reduce<SystemConfigType<T>[]>(
        (acc, row) => {
          const parseResult = safeJSONParse<T>(row.data, `SQLite:getAllSystemConfigs:${row.type}`);

          if (parseResult.success) {
            acc.push({
              id: row.id,
              type: row.type,
              data: parseResult.data,
            });
          } else {
            // Log but continue with other configs
            console.error(`Skipping corrupted config '${row.type}':`, parseResult.error);
          }

          return acc;
        },
        []
      );

      return configs;
    } catch (error) {
      console.error('Error in getAllSystemConfigs:', error);
      return [];
    }
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM system_config WHERE type = ?', [type]);
    } catch (error) {
      console.error('Error deleting system config:', error);
    }
  }

  public async getSystemConfigSnapshot<T>(type: string): Promise<SystemConfigSnapshot<T> | null> {
    const db = await this.getDB();
    const result = await db.query('SELECT * FROM system_config WHERE type = ?', [type]);
    if (!result.values) throw new Error('System config read was not acknowledged.');
    if (result.values.length > 1) throw new Error('System config has duplicate keys.');
    const row = result.values[0];
    if (!row) return null;
    if (!Number.isInteger(row.id) || typeof row.data !== 'string') {
      throw new Error('System config storage is malformed.');
    }
    return { id: row.id, type: row.type, data: JSON.parse(row.data), revision: row.data };
  }

  public async listSystemConfigSnapshots<T>(prefix: string): Promise<SystemConfigSnapshot<T>[]> {
    const db = await this.getDB();
    const result = await db.query('SELECT * FROM system_config WHERE substr(type, 1, ?) = ?', [
      prefix.length,
      prefix,
    ]);
    if (!result.values) throw new Error('System config read was not acknowledged.');
    return result.values.map((row) => ({
      id: row.id,
      type: row.type,
      data: JSON.parse(row.data),
      revision: row.data,
    }));
  }

  public async deleteSystemConfigIfUnchanged(
    expected: SystemConfigSnapshot<unknown>,
    canDelete: () => void
  ): Promise<ConditionalConfigDeletion> {
    const db = await this.getDB();
    canDelete();
    const result = await db.run(
      'DELETE FROM system_config WHERE id = ? AND type = ? AND data = ?',
      [expected.id, expected.type, expected.revision]
    );
    if (result.changes?.changes === 1) return 'deleted';
    if (result.changes?.changes !== 0)
      throw new Error('System config deletion was not acknowledged.');
    return (await this.getSystemConfigSnapshot(expected.type)) ? 'changed' : 'absent';
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM system_config');
    } catch (error) {
      console.error('Error deleting all system configs:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // inventory table helpers
  // ---------------------------------------------------------------------------

  // Idempotent — tables are already created in checkAndCreateDatabase().
  public async ensureInventoryTable(): Promise<void> {
    await this.initializeDB();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run(INVENTORY_UPSERT_SQL, buildInventoryValues(product));
    } catch (error) {
      console.error('Error in upsertInventoryItem:', error);
    }
  }

  // Uses executeSet to send all rows in a single Capacitor bridge call.
  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    if (products.length === 0) return;
    try {
      const db = await this.getDB();
      const set: capSQLiteSet[] = products.map((p) => buildInventorySet(p));
      await db.executeSet(set, true); // true = wrap in transaction
    } catch (error) {
      console.error('Error in bulkUpsertInventory:', error);
      throw error;
    }
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM inventory WHERE product_id = ?', [productId]);
    } catch (error) {
      console.error('Error in hardDeleteInventoryItem:', error);
    }
  }

  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
    taxable?: boolean;
    ebtEligible?: boolean;
    soldByWeight?: boolean;
    variablePrice?: boolean;
    productType?: ProductDataType['type'];
    sortBy?: 'recent' | 'name';
  }): Promise<{ items: ProductDataType[]; total: number }> {
    try {
      const db = await this.getDB();
      const {
        page,
        pageSize,
        search,
        category,
        taxable,
        ebtEligible,
        soldByWeight,
        variablePrice,
        productType,
        sortBy,
      } = params;
      const offset = page * pageSize;
      // Empty search → match everything via '%'; non-empty → substring match
      const like = search ? `%${search}%` : '%';
      // 'all' or undefined → skip category filter via sentinel value
      const catParam = category && category !== 'all' ? category : 'all';

      // Shared WHERE clause + params for both the COUNT and data queries.
      // Attribute filters live in the JSON `data` column, so they are matched with
      // json_extract — which yields 1/0 for JSON booleans and NULL for missing keys,
      // hence COALESCE to each attribute's schema default.
      let where = `deleted_at IS NULL
           AND (name LIKE ? OR identifier LIKE ?)
           AND (? = 'all' OR LOWER(TRIM(item_category)) = LOWER(TRIM(?)))`;
      const whereParams: (string | number)[] = [like, like, catParam, catParam];

      if (productType !== undefined) {
        where += `\n           AND json_extract(data, '$.type') = ?`;
        whereParams.push(productType);
      }
      if (taxable !== undefined) {
        where += `\n           AND COALESCE(json_extract(data, '$.attributes.isTaxable'), 1) = ?`;
        whereParams.push(taxable ? 1 : 0);
      }
      if (ebtEligible !== undefined) {
        where += `\n           AND COALESCE(json_extract(data, '$.attributes.ebtEligible'), 0) = ?`;
        whereParams.push(ebtEligible ? 1 : 0);
      }
      if (soldByWeight !== undefined) {
        where += `\n           AND COALESCE(json_extract(data, '$.attributes.physicalGood.soldByWeight'), 0) = ?`;
        whereParams.push(soldByWeight ? 1 : 0);
      }
      if (variablePrice !== undefined) {
        where += `\n           AND COALESCE(json_extract(data, '$.attributes.physicalGood.variablePrice'), 0) = ?`;
        whereParams.push(variablePrice ? 1 : 0);
      }

      const countRes = await db.query(
        `SELECT COUNT(*) as total FROM inventory WHERE ${where}`,
        whereParams
      );
      const total: number = countRes.values?.[0]?.total ?? 0;

      // Recency: timestamps are stored as ISO strings in the JSON `data` blob, so a
      // lexicographic DESC is chronological (newest first); missing values are SQL NULL,
      // which sorts last under DESC. Falls back to the alphabetical default otherwise.
      const orderBy =
        sortBy === 'recent'
          ? `ORDER BY COALESCE(json_extract(data, '$.updatedAt'), json_extract(data, '$.createdAt')) DESC,
                  name COLLATE NOCASE ASC`
          : `ORDER BY name COLLATE NOCASE ASC`;

      const dataRes = await db.query(
        `SELECT data FROM inventory
         WHERE ${where}
         ${orderBy}
         LIMIT ? OFFSET ?`,
        [...whereParams, pageSize, offset]
      );

      const items: ProductDataType[] = (dataRes.values ?? []).reduce<ProductDataType[]>(
        (acc, row) => {
          const parseResult = safeJSONParse<ProductDataType>(row.data, 'SQLite:queryInventoryPage');
          if (parseResult.success) acc.push(parseResult.data);
          return acc;
        },
        []
      );

      return { items, total };
    } catch (error) {
      console.error('Error in queryInventoryPage:', error);
      return { items: [], total: 0 };
    }
  }

  public async countInventory(): Promise<number> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NULL'
      );
      return res.values?.[0]?.total ?? 0;
    } catch (error) {
      console.error('Error in countInventory:', error);
      return 0;
    }
  }

  public async sumInventoryStock(productType?: ProductDataType['type']): Promise<number> {
    try {
      const db = await this.getDB();
      const typeFilter = productType === undefined ? '' : ` AND json_extract(data, '$.type') = ?`;
      const params = productType === undefined ? [] : [productType];
      const res = await db.query(
        `SELECT SUM(stock) as total FROM inventory WHERE deleted_at IS NULL${typeFilter}`,
        params
      );
      return res.values?.[0]?.total ?? 0;
    } catch (error) {
      console.error('Error in sumInventoryStock:', error);
      return 0;
    }
  }

  public async clearAllInventory(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM inventory');
    } catch (error) {
      console.error('Error in clearAllInventory:', error);
    }
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE product_id = ? AND deleted_at IS NULL',
        [productId]
      );
      if (!res.values || res.values.length === 0) return null;
      const parseResult = safeJSONParse<ProductDataType>(
        res.values[0].data,
        'SQLite:getInventoryById'
      );
      return parseResult.success ? parseResult.data : null;
    } catch (error) {
      console.error('Error in getInventoryById:', error);
      return null;
    }
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE identifier = ? AND deleted_at IS NULL',
        [identifier]
      );
      return pickNewestInventoryRow(res.values, 'SQLite:getInventoryByIdentifier');
    } catch (error) {
      console.error('Error in getInventoryByIdentifier:', error);
      return null;
    }
  }

  public async getInventoryByVariablePriceLookupCode(
    lookupCode: string
  ): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE variable_price_lookup_code = ? AND deleted_at IS NULL',
        [lookupCode]
      );
      return pickNewestInventoryRow(res.values, 'SQLite:getInventoryByVariablePriceLookupCode');
    } catch (error) {
      console.error('Error in getInventoryByVariablePriceLookupCode:', error);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats — derived cache
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(
    productType?: ProductDataType['type']
  ): Promise<Array<{ category: string; count: number }>> {
    try {
      const db = await this.getDB();
      const typeFilter =
        productType === undefined ? '' : `\n           AND json_extract(data, '$.type') = ?`;
      const params = productType === undefined ? [] : [productType];
      const res = await db.query(
        `SELECT
           COALESCE(NULLIF(TRIM(item_category), ''), 'GENERAL') AS category,
           COUNT(*) AS count
         FROM inventory
         WHERE deleted_at IS NULL${typeFilter}
         GROUP BY COALESCE(NULLIF(TRIM(item_category), ''), 'GENERAL')
         ORDER BY category COLLATE NOCASE ASC`,
        params
      );
      return (res.values ?? []).map((row) => ({
        category: String(row.category),
        count: Number(row.count),
      }));
    } catch (error) {
      console.error('Error in getInventoryCategoryCounts:', error);
      return [];
    }
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    try {
      const db = await this.getDB();

      const [totalRes, activeRes, deletedRes, stockRes, categoryCounts] = await Promise.all([
        db.query('SELECT COUNT(*) as total FROM inventory'),
        db.query('SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NULL'),
        db.query('SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NOT NULL'),
        db.query('SELECT SUM(stock) as total FROM inventory WHERE deleted_at IS NULL'),
        this.getInventoryCategoryCounts(),
      ]);

      const stats: InventoryStats = {
        id: 'inventory_stats',
        totalItems: Number(totalRes.values?.[0]?.total ?? 0),
        totalActiveItems: Number(activeRes.values?.[0]?.total ?? 0),
        totalDeletedItems: Number(deletedRes.values?.[0]?.total ?? 0),
        totalStockQuantity: Number(stockRes.values?.[0]?.total ?? 0),
        categoryCounts,
        lastCalculatedAt: new Date().toISOString(),
      };

      await db.run(
        `INSERT OR REPLACE INTO business_inventory_stats
           (id, total_items, total_active_items, total_deleted_items,
            total_stock_quantity, category_counts, last_calculated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          stats.id,
          stats.totalItems,
          stats.totalActiveItems,
          stats.totalDeletedItems,
          stats.totalStockQuantity,
          JSON.stringify(stats.categoryCounts),
          stats.lastCalculatedAt,
        ]
      );

      return stats;
    } catch (error) {
      console.error('Error in recalculateInventoryStats:', error);
      return {
        id: 'inventory_stats',
        totalItems: 0,
        totalActiveItems: 0,
        totalDeletedItems: 0,
        totalStockQuantity: 0,
        categoryCounts: [],
        lastCalculatedAt: new Date().toISOString(),
      };
    }
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const { recalculateIfMissing = true, forceRecalculate = false } = options ?? {};

    if (forceRecalculate) {
      return this.recalculateInventoryStats();
    }

    try {
      const db = await this.getDB();
      const res = await db.query(
        `SELECT * FROM business_inventory_stats WHERE id = 'inventory_stats'`
      );

      if (res.values && res.values.length > 0) {
        const row = res.values[0];
        const parseResult = safeJSONParse<Array<{ category: string; count: number }>>(
          row.category_counts,
          'SQLite:getInventoryStats:category_counts'
        );
        return {
          id: row.id,
          totalItems: Number(row.total_items ?? 0),
          totalActiveItems: Number(row.total_active_items ?? 0),
          totalDeletedItems: Number(row.total_deleted_items ?? 0),
          totalStockQuantity: Number(row.total_stock_quantity ?? 0),
          categoryCounts: parseResult.success ? parseResult.data : [],
          lastCalculatedAt: row.last_calculated_at,
          inventoryVersion: row.inventory_version ?? undefined,
        };
      }

      if (recalculateIfMissing) {
        return await this.recalculateInventoryStats();
      }
    } catch (error) {
      console.error('Error in getInventoryStats:', error);
    }

    return {
      id: 'inventory_stats',
      totalItems: 0,
      totalActiveItems: 0,
      totalDeletedItems: 0,
      totalStockQuantity: 0,
      categoryCounts: [],
      lastCalculatedAt: new Date().toISOString(),
    };
  }

  // ---------------------------------------------------------------------------
  // admin_businesses — stubs (admin panel is web-only)
  // ---------------------------------------------------------------------------

  // eslint-disable-next-line class-methods-use-this
  public async bulkReplaceAdminBusinesses(_businesses: BusinessV3Item[]): Promise<void> {
    // no-op: admin panel runs in web browser only
  }

  // eslint-disable-next-line class-methods-use-this
  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public async clearAdminBusinesses(): Promise<void> {
    // no-op: admin panel runs in web browser only
  }

  // ---------------------------------------------------------------------------
  // restaurant_order_enrichment_cache — business-scoped 24-hour sale snapshots
  // ---------------------------------------------------------------------------

  public async getRestaurantOrderEnrichmentCache(
    businessId: string,
    saleId: string
  ): Promise<RestaurantOrderEnrichmentCacheRecord | null> {
    try {
      const db = await this.getDB();
      const result = await db.query(
        `SELECT * FROM restaurant_order_enrichment_cache
         WHERE business_id = ? AND sale_id = ? LIMIT 1`,
        [businessId, saleId]
      );
      const row = result.values?.[0];
      if (!row) return null;
      const parsed = safeJSONParse<unknown>(
        row.payload,
        `SQLite:getRestaurantOrderEnrichmentCache:${businessId}:${saleId}`
      );
      if (!parsed.success) return null;
      return {
        cacheKey: row.cache_key,
        businessId: row.business_id,
        saleId: row.sale_id,
        payload: parsed.data,
        sourceUpdatedAt: row.source_updated_at ?? null,
        fetchedAt: row.fetched_at,
        expiresAt: row.expires_at,
      };
    } catch (error) {
      console.error('Error reading restaurant order enrichment cache:', error);
      return null;
    }
  }

  public async upsertRestaurantOrderEnrichmentCache(
    record: RestaurantOrderEnrichmentCacheRecord
  ): Promise<void> {
    const db = await this.getDB();
    await db.run(
      `INSERT INTO restaurant_order_enrichment_cache
        (cache_key, business_id, sale_id, payload, source_updated_at, fetched_at, expires_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(cache_key) DO UPDATE SET
         payload = excluded.payload,
         source_updated_at = excluded.source_updated_at,
         fetched_at = excluded.fetched_at,
         expires_at = excluded.expires_at`,
      [
        record.cacheKey,
        record.businessId,
        record.saleId,
        JSON.stringify(record.payload),
        record.sourceUpdatedAt,
        record.fetchedAt,
        record.expiresAt,
      ]
    );
  }

  public async deleteExpiredRestaurantOrderEnrichmentCache(beforeIso: string): Promise<number> {
    const db = await this.getDB();
    const result = await db.run(
      'DELETE FROM restaurant_order_enrichment_cache WHERE expires_at <= ?',
      [beforeIso]
    );
    return result.changes?.changes ?? 0;
  }

  public async clearRestaurantOrderEnrichmentCache(): Promise<void> {
    const db = await this.getDB();
    await db.run('DELETE FROM restaurant_order_enrichment_cache');
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_outbox — approved local payments awaiting handoff
  // ---------------------------------------------------------------------------

  public async upsertCompletedSaleReportOutbox(
    record: CompletedSaleReportOutboxRecord
  ): Promise<void> {
    const db = await this.getDB();
    await db.run(
      `INSERT INTO completed_sale_report_outbox
        (report_idempotency_key, business_id, state, created_at, handed_off_at, data)
       VALUES (?, ?, ?, ?, ?, ?)
       ON CONFLICT(report_idempotency_key) DO UPDATE SET
         business_id = excluded.business_id,
         state = excluded.state,
         created_at = excluded.created_at,
         handed_off_at = excluded.handed_off_at,
         data = excluded.data`,
      [
        record.reportIdempotencyKey,
        record.businessId,
        record.state,
        record.createdAt,
        record.handedOffAt,
        JSON.stringify(record),
      ]
    );
  }

  public async getCompletedSaleReportOutbox(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportOutboxRecord | null> {
    const db = await this.getDB();
    const result = await db.query(
      'SELECT data FROM completed_sale_report_outbox WHERE report_idempotency_key = ? LIMIT 1',
      [reportIdempotencyKey]
    );
    if (!result.values) throw new Error('Completed report read was not acknowledged.');
    const row = result.values?.[0];
    if (!row) return null;
    // Immutable report evidence must not be mistaken for absence when it is corrupt.
    return JSON.parse(row.data) as CompletedSaleReportOutboxRecord;
  }

  public async listCompletedSaleReportOutbox(
    businessId: string
  ): Promise<CompletedSaleReportOutboxRecord[]> {
    const db = await this.getDB();
    const result = await db.query(
      `SELECT data FROM completed_sale_report_outbox
       WHERE business_id = ? ORDER BY created_at ASC`,
      [businessId]
    );
    return (result.values ?? []).reduce<CompletedSaleReportOutboxRecord[]>((records, row) => {
      const parsed = safeJSONParse<CompletedSaleReportOutboxRecord>(
        row.data,
        `SQLite:listCompletedSaleReportOutbox:${businessId}`
      );
      if (parsed.success) records.push(parsed.data);
      return records;
    }, []);
  }

  public async deleteCompletedSaleReportOutbox(reportIdempotencyKey: string): Promise<void> {
    const db = await this.getDB();
    await db.run('DELETE FROM completed_sale_report_outbox WHERE report_idempotency_key = ?', [
      reportIdempotencyKey,
    ]);
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_history — bounded audit of successful ownership transfers
  // ---------------------------------------------------------------------------

  public async getCompletedSaleReportHistory(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportHistoryRecord | null> {
    const db = await this.getDB();
    const result = await db.query(
      'SELECT data FROM completed_sale_report_history WHERE report_idempotency_key = ? LIMIT 1',
      [reportIdempotencyKey]
    );
    if (!result.values) throw new Error('Completed report history read was not acknowledged.');
    const row = result.values?.[0];
    if (!row) return null;
    return JSON.parse(row.data) as CompletedSaleReportHistoryRecord;
  }

  public async listCompletedSaleReportHistory(
    businessId: string
  ): Promise<CompletedSaleReportHistoryRecord[]> {
    const db = await this.getDB();
    const result = await db.query(
      `SELECT data FROM completed_sale_report_history
       WHERE business_id = ? ORDER BY completed_at DESC`,
      [businessId]
    );
    return (result.values ?? []).reduce<CompletedSaleReportHistoryRecord[]>((records, row) => {
      const parsed = safeJSONParse<CompletedSaleReportHistoryRecord>(
        row.data,
        `SQLite:listCompletedSaleReportHistory:${businessId}`
      );
      if (parsed.success) records.push(parsed.data);
      return records;
    }, []);
  }

  public async completeCompletedSaleReportOutbox(
    record: CompletedSaleReportHistoryRecord
  ): Promise<void> {
    const db = await this.getDB();
    await db.executeSet(
      [
        {
          statement: `INSERT INTO completed_sale_report_history
            (report_idempotency_key, business_id, resolution, completed_at, data)
           VALUES (?, ?, ?, ?, ?)
           ON CONFLICT(report_idempotency_key) DO NOTHING`,
          values: [
            record.reportIdempotencyKey,
            record.businessId,
            record.resolution,
            record.completedAt,
            JSON.stringify(record),
          ],
        },
        {
          statement: 'DELETE FROM completed_sale_report_outbox WHERE report_idempotency_key = ?',
          values: [record.reportIdempotencyKey],
        },
        {
          statement: `DELETE FROM completed_sale_report_history
            WHERE report_idempotency_key IN (
              SELECT report_idempotency_key FROM completed_sale_report_history
              ORDER BY completed_at DESC, report_idempotency_key DESC
              LIMIT -1 OFFSET ?
            )`,
          values: [COMPLETED_SALE_REPORT_HISTORY_LIMIT],
        },
      ],
      true
    );
  }

  // ---------------------------------------------------------------------------
  // offline_mutation_outbox — typed, replay-safe operational API mutations
  // ---------------------------------------------------------------------------

  public async insertOfflineMutation(
    record: NewOfflineMutationRecord
  ): Promise<OfflineMutationRecord> {
    const db = await this.getDB();
    const result = await db.run(
      `INSERT INTO offline_mutation_outbox
        (mutation_id, business_id, business_device_id, operation, state, created_at, next_attempt_at, data)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        record.mutationId,
        record.businessId,
        record.businessDeviceId,
        record.operation,
        record.state,
        record.createdAt,
        record.nextAttemptAt,
        JSON.stringify(record),
      ]
    );
    const sequence = result.changes?.lastId;
    if (!Number.isInteger(sequence)) {
      throw new Error('Offline mutation storage did not assign a FIFO sequence');
    }
    const stored = { ...record, sequence } as OfflineMutationRecord;
    await this.updateOfflineMutation(stored);
    return stored;
  }

  public async updateOfflineMutation(record: OfflineMutationRecord): Promise<void> {
    const db = await this.getDB();
    await db.run(
      `UPDATE offline_mutation_outbox SET
         business_id = ?, business_device_id = ?, operation = ?, state = ?,
         created_at = ?, next_attempt_at = ?, data = ?
       WHERE mutation_id = ?`,
      [
        record.businessId,
        record.businessDeviceId,
        record.operation,
        record.state,
        record.createdAt,
        record.nextAttemptAt,
        JSON.stringify(record),
        record.mutationId,
      ]
    );
  }

  public async listOfflineMutations(businessId: string): Promise<OfflineMutationRecord[]> {
    const db = await this.getDB();
    const result = await db.query(
      `SELECT sequence, data FROM offline_mutation_outbox
       WHERE business_id = ? ORDER BY sequence ASC`,
      [businessId]
    );
    return (result.values ?? []).reduce<OfflineMutationRecord[]>((records, row) => {
      const parsed = safeJSONParse<Omit<OfflineMutationRecord, 'sequence'>>(
        row.data,
        `SQLite:listOfflineMutations:${businessId}`
      );
      if (parsed.success) {
        records.push({ ...parsed.data, sequence: Number(row.sequence) } as OfflineMutationRecord);
      }
      return records;
    }, []);
  }

  public async deleteOfflineMutation(mutationId: string): Promise<void> {
    const db = await this.getDB();
    await db.run('DELETE FROM offline_mutation_outbox WHERE mutation_id = ?', [mutationId]);
  }

  // ---------------------------------------------------------------------------
  // diagnostic_log_records — device diagnostic logging
  // ---------------------------------------------------------------------------

  private static toDiagnosticLogRow(row: any): DiagnosticLogRecordRow | null {
    let payload: DiagnosticLogRecordPayload;
    try {
      payload = JSON.parse(row.data);
    } catch {
      payload = { message: '' };
    }
    return {
      seq: Number(row.seq),
      recordId: String(row.record_id),
      businessId: String(row.business_id),
      sessionId: String(row.session_id),
      occurredAt: String(row.occurred_at),
      level: row.level,
      channel: row.channel,
      state: row.state,
      sizeBytes: Number(row.size_bytes),
      payload,
    };
  }

  /** Diagnostics may await startup owned by boot, never initialize or reopen storage. */
  private async getDiagnosticDB(admit: DiagnosticAdmission): Promise<SQLiteDBConnection> {
    assertDiagnosticAdmission(admit);
    if (!this.databaseReadyPromise) throw new DiagnosticIODeferred();
    await this.databaseReadyPromise;
    assertDiagnosticAdmission(admit);
    if (!this.initialized || !this.db) throw new DiagnosticIODeferred();
    return this.db;
  }

  /** One transaction per batch: every record write is a coalesced flush. */
  public async insertDiagnosticLogRecords(
    records: readonly NewDiagnosticLogRecordRow[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (records.length === 0) return;
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    const statements = records.map((record) => ({
      statement: `INSERT OR IGNORE INTO diagnostic_log_records
        (record_id, business_id, session_id, occurred_at, level, channel, state, size_bytes, data)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      values: [
        record.recordId,
        record.businessId,
        record.sessionId,
        record.occurredAt,
        record.level,
        record.channel,
        record.state,
        record.sizeBytes,
        JSON.stringify(record.payload),
      ],
    }));
    assertDiagnosticAdmission(admit);
    await db.executeSet(statements, true);
  }

  public async readDiagnosticLogPage(
    query: DiagnosticLogPageQuery,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    const db = await this.getDiagnosticDB(admit);
    const where = ['business_id = ?', "state = 'PENDING'"];
    const values: (string | number)[] = [query.businessId];
    if (query.beforeSequence !== undefined) {
      where.push('seq < ?');
      values.push(query.beforeSequence);
    }
    if (query.level) {
      where.push('level = ?');
      values.push(query.level);
    }
    if (query.channel) {
      where.push('channel = ?');
      values.push(query.channel);
    }
    const limit = Number.isFinite(query.limit)
      ? Math.min(101, Math.max(1, Math.floor(query.limit)))
      : 51;
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      `SELECT * FROM diagnostic_log_records WHERE ${where.join(' AND ')} ORDER BY seq DESC LIMIT ?`,
      [...values, limit]
    );
    return (result.values ?? []).flatMap((row) => {
      const record = SQLiteService.toDiagnosticLogRow(row);
      return record ? [record] : [];
    });
  }

  public async claimDiagnosticLogRecords(
    businessId: string,
    limit: number,
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      `SELECT * FROM diagnostic_log_records
       WHERE business_id = ? AND state = 'PENDING'
       ORDER BY seq ASC LIMIT ?`,
      [businessId, limit]
    );
    const claimed: DiagnosticLogRecordRow[] = [];
    let bytes = 0;
    (result.values ?? []).some((row) => {
      const record = SQLiteService.toDiagnosticLogRow(row);
      if (!record) return false;
      if (claimed.length > 0 && bytes + record.sizeBytes > maxBytes) return true;
      bytes += record.sizeBytes;
      claimed.push(record);
      return false;
    });
    return claimed;
  }

  public async deleteDiagnosticLogRecords(
    recordIds: readonly string[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (recordIds.length === 0) return;
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    await chunkArray([...recordIds], 200).reduce(async (previous, chunk) => {
      await previous;
      const placeholders = chunk.map(() => '?').join(', ');
      assertDiagnosticAdmission(admit);
      await db.run(
        `DELETE FROM diagnostic_log_records WHERE record_id IN (${placeholders})`,
        chunk
      );
    }, Promise.resolve());
  }

  public async deleteDiagnosticLogRecordsOlderThan(
    occurredBeforeIso: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.run('DELETE FROM diagnostic_log_records WHERE occurred_at <= ?', [
      occurredBeforeIso,
    ]);
    return result.changes?.changes ?? 0;
  }

  public async deleteDiagnosticLogRecordsForOtherBusinesses(
    businessId: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.run('DELETE FROM diagnostic_log_records WHERE business_id != ?', [
      businessId,
    ]);
    return result.changes?.changes ?? 0;
  }

  /** Deletes oldest-first until the stored total fits under `maxBytes`. */
  public async trimDiagnosticLogRecordsToBytes(
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<number> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      'SELECT seq, size_bytes FROM diagnostic_log_records ORDER BY seq ASC'
    );
    const rows = result.values ?? [];
    const total = rows.reduce((sum, row) => sum + Number(row.size_bytes), 0);
    if (total <= maxBytes) return 0;

    let remaining = total;
    let cutoffSeq: number | null = null;
    let removed = 0;
    rows.some((row) => {
      if (remaining <= maxBytes) return true;
      remaining -= Number(row.size_bytes);
      cutoffSeq = Number(row.seq);
      removed += 1;
      return false;
    });
    if (cutoffSeq === null) return 0;

    assertDiagnosticAdmission(admit);

    await db.run('DELETE FROM diagnostic_log_records WHERE seq <= ?', [cutoffSeq]);
    return removed;
  }

  public async getDiagnosticLogStorageStats(
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogStorageStats> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      `SELECT COUNT(*) AS count, COALESCE(SUM(size_bytes), 0) AS total_bytes,
              MIN(occurred_at) AS oldest, MAX(occurred_at) AS newest
       FROM diagnostic_log_records`
    );
    const row = result.values?.[0] ?? {};
    assertDiagnosticAdmission(admit);
    const newestBusiness = await db.query(
      'SELECT business_id FROM diagnostic_log_records ORDER BY seq DESC LIMIT 1'
    );
    return {
      count: Number(row.count ?? 0),
      totalBytes: Number(row.total_bytes ?? 0),
      oldestOccurredAt: row.oldest ? String(row.oldest) : null,
      newestOccurredAt: row.newest ? String(row.newest) : null,
      newestBusinessId: newestBusiness.values?.[0]?.business_id
        ? String(newestBusiness.values[0].business_id)
        : null,
    };
  }

  public async clearDiagnosticLogRecords(admit: DiagnosticAdmission): Promise<void> {
    assertDiagnosticAdmission(admit);
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    await db.run('DELETE FROM diagnostic_log_records');
  }

  /* eslint-disable no-await-in-loop, no-restricted-syntax -- Preserve sequential, admission-checked bridge and transactional operations. */
  public async readDiagnosticRecords(
    ids: string[],
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    const db = await this.getDiagnosticDB(admit);
    const records: DiagnosticLogRecordRow[] = [];
    for (const chunk of chunkArray(ids, 200)) {
      assertDiagnosticAdmission(admit);
      const result = await db.query(
        `SELECT * FROM diagnostic_log_records WHERE record_id IN (${chunk.map(() => '?').join(',')})`,
        chunk
      );
      records.push(
        ...(result.values ?? []).flatMap((row) => SQLiteService.toDiagnosticLogRow(row) ?? [])
      );
    }
    return records;
  }

  public async readDiagnosticMaintenanceRows(
    query: { limit: number; afterSequence?: number },
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      'SELECT * FROM diagnostic_log_records WHERE seq > ? ORDER BY seq ASC LIMIT ?',
      [query.afterSequence ?? 0, query.limit]
    );
    return (result.values ?? []).flatMap((row) => SQLiteService.toDiagnosticLogRow(row) ?? []);
  }

  public async listDiagnosticBatches(
    scope: DiagnosticScope,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticUploadBatch[]> {
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      'SELECT data FROM diagnostic_upload_batches WHERE business_id = ? AND device_id = ?',
      [scope.businessId, scope.businessDeviceId]
    );
    return (result.values ?? []).map((row) => JSON.parse(row.data));
  }

  public async readDiagnosticDeliveryState(
    id: string,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticDeliveryState | undefined> {
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query('SELECT data FROM diagnostic_delivery_state WHERE id = ?', [id]);
    return result.values?.[0] ? JSON.parse(result.values[0].data) : undefined;
  }

  public async readDiagnosticDropAlerts(
    scope: DiagnosticScope,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticDropAlert[]> {
    const db = await this.getDiagnosticDB(admit);
    assertDiagnosticAdmission(admit);
    const result = await db.query(
      'SELECT seq, data FROM diagnostic_drop_alerts WHERE business_id = ? AND device_id = ? ORDER BY seq DESC LIMIT ?',
      [scope.businessId, scope.businessDeviceId, DIAGNOSTIC_DROP_HISTORY_LIMIT]
    );
    return (result.values ?? []).map((row) => ({ ...JSON.parse(row.data), seq: row.seq }));
  }

  public async mutateDiagnosticDelivery(
    change: DiagnosticDeliveryMutation,
    admit: DiagnosticAdmission
  ): Promise<void> {
    const db = await this.getDiagnosticDB(admit);
    const statements: capSQLiteSet[] = [];
    if (change.purgeOtherScopes) {
      const scope = change.purgeOtherScopes;
      for (const table of [
        'diagnostic_upload_batches',
        'diagnostic_delivery_state',
        'diagnostic_drop_alerts',
      ])
        statements.push({
          statement: `DELETE FROM ${table} WHERE business_id != ? OR device_id != ?`,
          values: [scope.businessId, scope.businessDeviceId],
        });
      statements.push({
        statement: 'DELETE FROM diagnostic_log_records WHERE business_id != ?',
        values: [scope.businessId],
      });
      // JSON1 is not available on every native adapter. Same-business capture
      // identity is validated by the bounded uploader/retention reads.
    }
    for (const [table, record] of [
      ['diagnostic_upload_batches', change.batch],
      ['diagnostic_delivery_state', change.state],
    ] as const) {
      if (record)
        statements.push({
          statement: `INSERT OR REPLACE INTO ${table} (id,business_id,device_id,data) VALUES (?,?,?,?)`,
          values: [record.id, record.businessId, record.businessDeviceId, JSON.stringify(record)],
        });
    }
    for (const chunk of chunkArray(change.batch?.ids ?? [], 200))
      statements.push({
        statement: `UPDATE diagnostic_log_records SET state = 'RESERVED' WHERE record_id IN (${chunk.map(() => '?').join(',')})`,
        values: chunk,
      });
    for (const chunk of chunkArray(change.deleteRecordIds ?? [], 200))
      statements.push({
        statement: `DELETE FROM diagnostic_log_records WHERE record_id IN (${chunk.map(() => '?').join(',')})`,
        values: chunk,
      });
    if (change.deleteBatchId)
      statements.push({
        statement: 'DELETE FROM diagnostic_upload_batches WHERE id = ?',
        values: [change.deleteBatchId],
      });
    for (const alert of change.alerts ?? [])
      statements.push({
        statement:
          'INSERT OR IGNORE INTO diagnostic_drop_alerts (id,business_id,device_id,data) VALUES (?,?,?,?)',
        values: [alert.id, alert.businessId, alert.businessDeviceId, JSON.stringify(alert)],
      });
    statements.push({
      statement:
        'DELETE FROM diagnostic_drop_alerts WHERE seq IN (SELECT seq FROM diagnostic_drop_alerts ORDER BY seq DESC LIMIT -1 OFFSET ?)',
      values: [DIAGNOSTIC_DROP_HISTORY_LIMIT],
    });
    assertDiagnosticAdmission(admit);
    await db.executeSet(statements, true);
  }
  /* eslint-enable no-await-in-loop, no-restricted-syntax */
}

export default SQLiteService;
